// src/pages/guides/WAFVulnerabilityScanningGuide.js
// PRD-56 follow-up — Help-center guide explaining WAF interactions with
// vulnerability scanning. Linked from the WafBlockingBanner "Learn more" CTA.

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function WAFVulnerabilityScanningGuide() {
  const startTrial = useTrialCta('guide_trial:waf-and-vulnerability-scanning');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "WAF and Vulnerability Scanning: How Web Application Firewalls Affect Your Scan Results",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-05-08T00:00:00-04:00",
    "dateModified": "2026-05-08T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/waf-and-vulnerability-scanning",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/waf-and-vulnerability-scanning",
    "keywords": [
      "WAF vulnerability scanning",
      "web application firewall scanner",
      "Cloudflare allowlist scanner",
      "vulnerability scanner WAF bypass",
      "WAF blocking scanner",
      "scanner allowlist",
      "Cloudflare WAF IP allowlist",
      "Akamai bypass rule",
      "Vercel firewall allowlist",
      "AWS WAF allowlist scanner"
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "WAF and Vulnerability Scanning" }
    ]
  };


  return (
    <div className="guide-container">
      <SEOHead
        title="WAF and Vulnerability Scanning: How Web Application Firewalls Affect Scan Results | DriftAlarm"
        description="Learn how Web Application Firewalls (Cloudflare, Vercel, Akamai, AWS WAF) interact with vulnerability scanners, why your scan may show fewer findings on WAF-protected sites, and how to allowlist authorized scanners for full coverage without weakening your defenses."
        path="/guides/waf-and-vulnerability-scanning"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/waf-and-vulnerability-scanning')].filter(Boolean)}
      />

      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>WAF and Vulnerability Scanning: How Web Application Firewalls Affect Your Scan Results</h1>
          <GuideByline path="/guides/waf-and-vulnerability-scanning" />
          <GuideAnswer path="/guides/waf-and-vulnerability-scanning" />
          <p className="guide-hero-intro">
            A Web Application Firewall sits in front of your application and blocks suspicious traffic
            before it reaches your origin. That includes traffic from vulnerability scanners — even
            authorized ones. When the WAF challenges or blocks our probes, DriftAlarm cannot test the
            underlying app, and your scan report shows fewer findings than the actual surface contains.
            This guide explains why that happens, how to read DriftAlarm's WAF banner, and the
            customer-trust-preserving way to get full coverage without weakening your defenses.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>How WAFs interact with external vulnerability scanners</li>
              <li>The three coverage modes: full, WAF-limited, and authenticated</li>
              <li>How to read the DriftAlarm WAF banner and what each vendor's challenge looks like</li>
              <li>How to allowlist authorized scanners in Cloudflare, Vercel, Akamai, and AWS WAF</li>
              <li>Why allowlisting your scanner does not weaken your defenses against attackers</li>
            </ul>
          </div>

          <span className="guide-read-time">9 min read</span>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/easm-vs-vulnerability-scanning" className="da-btn da-btn--secondary">
              Read EASM vs Vulnerability Scanning
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#short-answer">The Short Answer</a></li>
            <li><a href="#how-wafs-affect-scans">How WAFs Affect Vulnerability Scans</a></li>
            <li><a href="#coverage-modes">Three Coverage Modes</a></li>
            <li><a href="#waf-banner">Reading the DriftAlarm WAF Banner</a></li>
            <li><a href="#workarounds">Workarounds: Allowlisting Authorized Scanners</a></li>
            <li><a href="#caveat">Important Caveat: WAFs Still Protect You</a></li>
            <li><a href="#authenticated-scanning">When to Consider Authenticated Scanning</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
            <li><a href="#related-guides">Related Guides</a></li>
          </ul>
        </nav>

        <section id="short-answer" className="guide-section">
          <h2>The Short Answer</h2>

          <p>
            If your DriftAlarm asset shows a yellow or red WAF banner, it means more than half of our
            probes against your application returned challenge or block responses from a Web Application
            Firewall — Cloudflare, Vercel, Akamai, AWS WAF, F5, Imperva, Fastly, or Azure App Service.
            <strong> The scanner is working correctly. So is your WAF.</strong> The scan report reflects
            only the WAF-bypassable surface of your application; everything behind the WAF is invisible
            to external probes.
          </p>

          <p>
            You have three choices: accept the limited coverage as confirmation that your edge defenses
            are working, allowlist DriftAlarm's scanner so we can probe the full application, or move to
            authenticated scanning where we use your session cookies to test the post-login surface.
            Most security teams choose to allowlist authorized scanners — it gives full visibility for
            you without weakening defenses against real attackers.
          </p>
        </section>

        <section id="how-wafs-affect-scans" className="guide-section">
          <h2>How WAFs Affect Vulnerability Scans</h2>

          <p>
            A Web Application Firewall inspects incoming HTTP requests and applies rules to decide
            whether to let them through, challenge them, or block them. Modern WAFs combine signature
            matching, behavioral analysis, IP reputation, TLS fingerprinting, and rate limiting. Most
            ship with default rules that flag traffic from known scanner IPs, requests carrying scanner
            User-Agent strings, and patterns that resemble probes for common vulnerabilities like SQL
            injection or path traversal.
          </p>

          <p>
            When DriftAlarm's scanner sends probes against an application behind a WAF, one of four
            things happens to each probe:
          </p>

          <ul>
            <li>
              <strong>Pass through:</strong> The WAF allows the request and our scanner sees the real
              application response. We can test for vulnerabilities on this surface.
            </li>
            <li>
              <strong>Challenge:</strong> The WAF returns an interactive challenge page (Cloudflare's
              "Just a moment...", Vercel Security Checkpoint, Akamai's Bot Manager challenge). Headless
              scanners cannot solve these challenges, so the response we see is the challenge page —
              not the application.
            </li>
            <li>
              <strong>Silent block:</strong> The WAF returns a generic 403 Forbidden or 503 Service
              Unavailable without revealing why. Cloudflare's custom firewall rules and AWS WAF default
              actions often work this way.
            </li>
            <li>
              <strong>Rate limit:</strong> The WAF accepts initial probes but starts returning 429 Too
              Many Requests after a threshold. DriftAlarm already throttles to 50 requests per second on
              its first pass when WAF presence is detected, but rate-limit-only WAFs are uncommon — most challenge or
              block based on signature, not rate.
            </li>
          </ul>

          <p>
            Each probe that gets challenged or blocked is a probe that didn't reach your application,
            so DriftAlarm cannot test that endpoint for vulnerabilities. Suppose 87% of probes return a
            challenge page: we then have meaningful scan data on only 13% of your URLs. The risk
            score and finding counts reflect that 13% — the real attack surface behind the WAF is
            invisible to our scan.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">This Is Not a Scanner Failure</div>
            <div className="callout-content">
              <p>
                A scanner that gets WAF-blocked is doing exactly what it should: respecting your edge
                defenses. The same posture that protects you from automated attacks also limits what
                authorized scanners can see. The fix is not a stronger scanner — the fix is to give
                authorized scanners explicit permission to bypass the WAF, while keeping protection
                against everything else.
              </p>
            </div>
          </div>
        </section>

        <section id="coverage-modes" className="guide-section">
          <h2>Three Coverage Modes</h2>

          <p>
            DriftAlarm coverage falls into one of three modes depending on how your application is
            configured and whether you've taken steps to grant the scanner access:
          </p>

          <h3>1. Full Coverage</h3>
          <p>
            No WAF in front of the application, or the application is internally exposed without an
            edge firewall. DriftAlarm probes every reachable URL and tests for vulnerabilities directly.
            Risk scores reflect the actual surface. This is the typical mode for IP-routed services,
            staging environments without WAF protection, and applications behind only a CDN cache (no
            WAF rules).
          </p>

          <h3>2. WAF-Limited Coverage</h3>
          <p>
            A WAF challenges or blocks our probes. DriftAlarm sees only the WAF response, not the
            application. The asset detail page shows a yellow or red WAF banner identifying the vendor
            and the percentage of blocked probes. <strong>Findings reflect only the WAF-bypassable
            surface</strong>, which on a heavily protected site is a small fraction of the application. This is the default
            state for production sites behind Cloudflare bot fight, Vercel checkpoint mode, AWS Shield
            Advanced, or similar.
          </p>

          <h3>3. Authenticated / Allowlisted Coverage</h3>
          <p>
            Either DriftAlarm has been allowlisted in your WAF (probes pass through to the application)
            or DriftAlarm has been provided with valid session credentials (probes are made as a logged-in
            user, often bypassing many WAF rules tied to anonymous traffic). This is the most accurate
            mode and matches what an authenticated attacker would see if your credentials leaked.
          </p>

          <p>
            Most production assets at small and mid-sized businesses run in mode 2 by default. Mode 3
            requires a one-time setup, and what it buys you is coverage: the scanner tests the
            application itself rather than the edge in front of it.
          </p>
        </section>

        <section id="waf-banner" className="guide-section">
          <h2>Reading the DriftAlarm WAF Banner</h2>

          <p>
            DriftAlarm displays a banner on the asset detail page when WAF challenge-blocking exceeds
            50% of probed URLs. The banner is color-coded and identifies the vendor:
          </p>

          <ul>
            <li>
              <strong>Yellow banner (50% to 90% blocked):</strong> Partial coverage. Some URLs are
              reaching the application; many are being challenged. Findings represent the bypassable
              subset, but the WAF-protected surface is invisible.
            </li>
            <li>
              <strong>Red banner (above 90% blocked):</strong> Severely limited coverage. The WAF is
              effectively a wall in front of your application. Scan findings are unreliable as a
              representation of overall risk because almost nothing reaches the origin.
            </li>
          </ul>

          <p>
            The banner also names the dominant WAF vendor based on response signatures. Each vendor has
            characteristic challenge or block responses:
          </p>

          <ul>
            <li><strong>Vercel:</strong> 403 with title "Vercel Security Checkpoint"</li>
            <li><strong>Cloudflare:</strong> 403 or 503 with various titles ("Just a moment...", "Attention Required! | Cloudflare", "Sorry, you have been blocked", or generic "Error 403 Forbidden" with cf-ray header)</li>
            <li><strong>Akamai:</strong> 403 with "Access Denied" or "Reference #" pattern</li>
            <li><strong>AWS WAF:</strong> 403 with "Request blocked"</li>
            <li><strong>Imperva / Incapsula:</strong> Page contains "Incapsula incident ID"</li>
            <li><strong>F5 / BIG-IP ASM:</strong> "The requested URL was rejected"</li>
            <li><strong>Fastly:</strong> 403 returned via Fastly CDN</li>
            <li><strong>Azure App Service:</strong> 403 with "Service unavailable" or "Web App - Unavailable"</li>
          </ul>

          <p>
            DriftAlarm also writes a <code>WAF-limited</code> badge on each scan history row when the
            asset is currently WAF-blocked, so you can see at a glance which scans were affected by
            edge defenses versus scans that probed the full surface.
          </p>
        </section>

        <section id="workarounds" className="guide-section">
          <h2>Workarounds: Allowlisting Authorized Scanners</h2>

          <p>
            The cleanest way to get full coverage is to allowlist DriftAlarm's scanner egress in your
            WAF. This tells your WAF to let our specific traffic through to the application, while
            keeping all other rules active for the rest of the internet. Each major WAF vendor supports
            scanner allowlisting through different mechanisms.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Request a Dedicated Scanner Egress IP</div>
            <div className="callout-content">
              <p>
                DriftAlarm currently uses a shared dynamic egress for the scanning fleet. To allowlist
                us reliably, you'll need a dedicated static IP that we route your scans through.
                <strong> This is available on request</strong> — contact us at{' '}
                <a href="mailto:info@driftalarm.com?subject=WAF%20Allowlist%20Request">
                  info@driftalarm.com
                </a>{' '}
                with your asset domain(s) and we'll provision a dedicated egress and share the IP for
                your allowlist rule. There's no extra charge on Pro tier; turnaround is typically a few
                business days.
              </p>
            </div>
          </div>

          <h3>Cloudflare</h3>
          <p>
            Cloudflare supports scanner allowlisting via <strong>IP Access Rules</strong> or by
            creating a custom firewall rule with a "Skip" action for your scanner's IP. The simplest
            approach is to add an IP Access Rule with action "Allow" scoped to the relevant zone or
            account.
          </p>
          <ul>
            <li>
              Cloudflare documentation:{' '}
              <a
                href="https://developers.cloudflare.com/waf/tools/ip-access-rules/"
                target="_blank"
                rel="noopener noreferrer"
              >
                IP Access Rules
              </a>
            </li>
            <li>
              For more granular control with WAF rule skipping:{' '}
              <a
                href="https://developers.cloudflare.com/waf/custom-rules/skip/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Skip action for custom rules
              </a>
            </li>
          </ul>
          <p>
            Allowlisting at the account level rather than per-zone is faster if you have many domains
            on Cloudflare.
          </p>

          <h3>Akamai</h3>
          <p>
            Akamai's bypass approach depends on your product (Kona Site Defender, App &amp; API
            Protector, Bot Manager). The most common pattern is a <strong>Network List</strong>{' '}
            allowlist combined with a match target rule that exempts traffic from that list.
          </p>
          <ul>
            <li>
              Akamai documentation:{' '}
              <a
                href="https://techdocs.akamai.com/security-policies/docs/about-network-lists"
                target="_blank"
                rel="noopener noreferrer"
              >
                About Network Lists
              </a>
            </li>
            <li>
              For Bot Manager exemptions:{' '}
              <a
                href="https://techdocs.akamai.com/bot-manager/docs/welcome"
                target="_blank"
                rel="noopener noreferrer"
              >
                Bot Manager documentation
              </a>
            </li>
          </ul>

          <h3>Vercel</h3>
          <p>
            Vercel's firewall (formerly Attack Challenge Mode) supports IP allowlisting through
            <strong> Firewall &gt; Rules</strong> in the project dashboard. Create a rule with action
            "Allow" and the condition matching DriftAlarm's egress IP.
          </p>
          <ul>
            <li>
              Vercel documentation:{' '}
              <a
                href="https://vercel.com/docs/security/vercel-firewall"
                target="_blank"
                rel="noopener noreferrer"
              >
                Vercel Firewall
              </a>
            </li>
            <li>
              Custom rules and IP allowlists:{' '}
              <a
                href="https://vercel.com/docs/security/vercel-firewall/custom-rules"
                target="_blank"
                rel="noopener noreferrer"
              >
                Custom rules
              </a>
            </li>
          </ul>

          <h3>AWS WAF</h3>
          <p>
            AWS WAF allowlisting is done by creating an <strong>IP set</strong> containing the
            scanner's IP, then adding a rule to your Web ACL that <strong>allows</strong> traffic
            matching that IP set, placed earlier in the rule priority than your blocking rules.
          </p>
          <ul>
            <li>
              AWS documentation:{' '}
              <a
                href="https://docs.aws.amazon.com/waf/latest/developerguide/waf-ip-set-creating.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Creating an IP set
              </a>
            </li>
            <li>
              Rule priority and matching:{' '}
              <a
                href="https://docs.aws.amazon.com/waf/latest/developerguide/web-acl-rule-actions.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Web ACL rule actions
              </a>
            </li>
          </ul>

          <h3>Other WAFs (Imperva, F5, Fastly, Azure)</h3>
          <p>
            All major WAF vendors support IP allowlisting through similar mechanisms — either an IP
            allow list, an exemption rule, or a "trusted source" group. Refer to your vendor
            documentation, or contact{' '}
            <a href="mailto:info@driftalarm.com">info@driftalarm.com</a> and we can help you find
            the right setting for your specific product.
          </p>
        </section>

        <section id="caveat" className="guide-section">
          <h2>Important Caveat: WAFs Still Protect You</h2>

          <p>
            Allowlisting DriftAlarm's scanner does not weaken your defenses against real attackers.
            The allowlist rule is scoped to a single, dedicated IP that only DriftAlarm uses. Every
            other source — including all attacker traffic — still hits the full WAF rule set. The WAF
            continues to block automated attacks, brute force attempts, signature-based exploits, and
            anomalous behavior from anywhere except our specific IP.
          </p>

          <p>
            What allowlisting does change is your <strong>scan visibility</strong>: DriftAlarm sees the
            same surface an authenticated, well-behaved client would see. That surface is precisely
            what an attacker would target if they obtained valid credentials, bypassed your WAF
            through a zero-day, or compromised an internal source already inside your trusted network.
            Knowing about those vulnerabilities gives you the chance to patch them before someone else
            uses them.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">The Two-Layer View</div>
            <div className="callout-content">
              <p>
                Think of WAF-blocked scans as showing your <strong>perimeter view</strong> and
                allowlisted scans as showing your <strong>interior view</strong>. Both are useful.
                Most security teams want both: they keep the WAF active to deflect everyday attacks,
                and they allowlist authorized scanners to find what's broken on the inside before
                someone with a foothold does.
              </p>
            </div>
          </div>
        </section>

        <section id="authenticated-scanning" className="guide-section">
          <h2>When to Consider Authenticated Scanning</h2>

          <p>
            Allowlisting DriftAlarm's scanner gives us full reach to your application's surface, but
            it doesn't get us past authentication walls. If your application's main risk surface is
            behind a login (think customer dashboards, admin portals, internal tools fronted by SSO),
            allowlisting alone won't reveal what's reachable post-auth.
          </p>

          <p>
            Authenticated scanning solves this by letting DriftAlarm probe with valid session cookies
            or tokens you provide. The scanner makes requests as a logged-in user, exposing the
            post-auth attack surface that regular external scans never see.
          </p>

          <p>
            DriftAlarm's authenticated scanning is on the roadmap and not yet generally available. If
            your security program needs authenticated coverage, reach out at{' '}
            <a href="mailto:info@driftalarm.com?subject=Authenticated%20Scanning%20Interest">
              info@driftalarm.com
            </a>{' '}
            and we'll add you to the early-access list and prioritize accordingly.
          </p>
        </section>

        <GuideFAQ path="/guides/waf-and-vulnerability-scanning" />

        <section id="related-guides" className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Continue building your understanding of vulnerability management and external attack
            surface monitoring:
          </p>
          <ul>
            <li>
              <Link to="/guides/easm-vs-vulnerability-scanning">EASM vs Vulnerability Scanning</Link> —
              Understand how External Attack Surface Management complements traditional vulnerability
              scanners and where each tool delivers the most value.
            </li>
            <li>
              <Link to="/guides/vulnerability-validation">Vulnerability Validation Guide</Link> — How
              to validate scanner findings before escalating, including verification techniques that
              work even when WAFs limit external visibility.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — The
              complete guide to continuous external attack surface monitoring and the P0-P4
              prioritization framework DriftAlarm uses.
            </li>
            <li>
              <Link to="/guides/manual-recon-vs-easm">Manual Recon vs Managed EASM</Link> — Time and
              skill cost comparison between manual reconnaissance and continuous automated EASM.
            </li>
            <li>
              <Link to="/external-vulnerability-scanning">External Vulnerability Scanning</Link> — How
              external vulnerability scanning works for internet-facing assets, including the
              limitations imposed by edge defenses like WAFs and CDNs.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Get Full Coverage Today</h2>
          <p>
            If your DriftAlarm scan is showing limited coverage because of WAF blocking, allowlisting
            is the fastest path to full visibility. Contact us and we'll provision a dedicated scanner
            egress IP for your account so you can add a single, scoped allowlist rule to your WAF.
          </p>
          <div className="guide-cta-group" style={{ marginTop: '24px' }}>
            <a
              href="mailto:info@driftalarm.com?subject=WAF%20Allowlist%20Request&body=Please%20provision%20a%20dedicated%20scanner%20egress%20IP%20for%20our%20account%20so%20we%20can%20allowlist%20DriftAlarm%20in%20our%20WAF.%20Asset%20domain(s):%20"
              className="da-btn da-btn--primary"
            >
              <span>Request a Dedicated Scanner IP</span>
              <span>&rarr;</span>
            </a>
            <button className="da-btn da-btn--secondary" onClick={startTrial}>
              Start Free 30-Day Trial
            </button>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

export default WAFVulnerabilityScanningGuide;
